import { WebDavServerModel, listServers } from "client/Api/Documents/WebDav/webDav";
import { DestinationWindow } from "client/Api/Apps/common";
import { getUrlForHistoryState } from "client/Features/Documents/DocumentPage";
import { WebDavSideBarMenu } from "client/Features/Documents/WebDav/WebDavSideBarMenu";
import { ServerLoadingState, WebDavHistoryState } from "client/Features/Documents/WebDav/WebDavStore";
import { AlertManager, useAlerts, useUnmountedCancellation } from "client/Hooks";
import { DocumentStoreType } from "client/Api/Documents/common";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { ReactElement, useCallback, useEffect, useState } from "react";

export type WebDavSectionProps = {
	hasInitializationError: boolean;
};

export const WebDavSection = (props: WebDavSectionProps): ReactElement => {
	const {
		hasInitializationError
	} = props;

	const [serverLoadingState, setServerLoadingState] = useState<ServerLoadingState>({ kind: "loading" });

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const loadServers = useCallback(
		async (): Promise<void> => {
			try {
				setServerLoadingState({ kind: "loading" });
				const servers: readonly WebDavServerModel[] = await listServers(globalCancellation);

				setServerLoadingState(
					servers.length > 0
						? {
							kind: "success",
							servers
						}
						: { kind: "error" }
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setServerLoadingState({ kind: "error" });
			}
		},
		[
			alertManager,
			globalCancellation
		]
	);

	useEffect(
		(): void => {
			if (hasInitializationError) {
				setServerLoadingState({ kind: "error" });
			} else {
				loadServers();
			}
		},
		[
			alertManager,
			hasInitializationError,
			loadServers
		]
	);

	const selectServerHandler = useCallback(
		(server: WebDavServerModel): void => {
			const state: WebDavHistoryState = {
				store: DocumentStoreType.WebDav,
				server: server
			};

			const url: string = getUrlForHistoryState(state);

			const { destinationWindow } = userConfiguration().document;

			navigateTo(
				url,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[]
	);

	return (
		<WebDavSideBarMenu
			serverLoadingState={serverLoadingState}
			onSelectServer={selectServerHandler}
			onReloadSideBar={loadServers}
		/>
	);
};
WebDavSection.displayName = "WebDavSection";